<!--
  DecorationLibraryLegacyRedirect.vue -- legacy URL bridge.

  Card: Boulders #163 (https://trello.com/c/S5kN5poU)
  Pebble: Phase P5 PR-B -- DecorationDetailPage method-tabbed re-skin.

  Mounted at the legacy ``/decoration-library/:id`` route. The legacy
  detail page (``DecorationDetailPage.vue``) treats ``:id`` as an
  artwork id (the underlying Pinia store fetches
  ``/api/artworks/<id>/``); so the redirect is mechanical -- swap
  the named route and keep the param.

  Why a component (rather than a router redirect)?
  ------------------------------------------------
  Vue Router's static ``redirect: { name: 'library_artwork_detail' }``
  cannot rename a param key from ``:id`` to ``:artwork_id`` because the
  redirect target is resolved at config-load time, before the
  destination route's param schema is known. The function-form
  redirect would work; we use a component for two reasons:
    1. Symmetry with the spec, which calls for a redirect resolver
       component (so the redirect can grow if the lookup later needs
       a backend call -- e.g., if a deep link points at a
       DecorationV2.id rather than an Artwork.id).
    2. Loading-state surface: the redirect renders briefly so a slow
       navigation does not surface a blank page.

  Tenant + auth
  -------------
  The destination route enforces its own capability gate
  (``distributor.decoration_library.read``); this redirect component
  does not need its own gate because it never renders user data.
-->
<template>
    <div
        class="dlr-redirect"
        data-testid="decoration-library-legacy-redirect"
    >
        <v-progress-circular indeterminate color="primary" />
        <span class="dlr-redirect__copy">Loading...</span>
    </div>
</template>

<script>
export default {
    name: 'DecorationLibraryLegacyRedirect',
    props: {
        id: {
            // The legacy route's param. Already an artwork id in the
            // current store + component contract.
            type: [String, Number],
            default: null,
        },
    },
    mounted() {
        const aid =
            this.id ?? this.$route?.params?.id ?? null
        if (aid == null) {
            // No id provided -- send the user back to the library
            // list rather than rendering a broken state.
            this.$router.replace({ name: 'decoration_library_list' })
            return
        }
        this.$router.replace({
            name: 'library_artwork_detail',
            params: { artwork_id: String(aid) },
        })
    },
}
</script>

<style scoped>
.dlr-redirect {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 240px;
    color: #6b7280;
}
.dlr-redirect__copy {
    font-size: 14px;
}
</style>
